import MapCompanies from "@/components/company/map-companies";
import DataTableCompanies from "@/components/data-table-companies/data-table-companies";
import ErrorMessage from "@/components/error-message";
import {
  MapControls,
  MapControlsItineraryState,
} from "@/components/itinerary/map-controls";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";
import { Separator } from "@/components/ui/separator";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { CompanyDTO } from "@shaarp/common";
import { useEffect, useState } from "react";

export default function HistoryPage() {
  const api = useApi();
  const { t } = useTranslation();

  const [companies, setCompanies] = useState<CompanyDTO[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const [isMapOpened, setIsMapOpened] = useState<boolean>(false);
  const [companySelected, setCompanySelected] = useState<
    CompanyDTO | undefined
  >(undefined);

  const [itineraryState, setItineraryState] =
    useState<MapControlsItineraryState | null>(null);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      // This page is the full history view (table + map), so it asks for the
      // endpoint's server-side maximum explicitly — with no `limit` at all it
      // now gets the endpoint's default page (20) instead of everything. The
      // limit counts distinct companies, so this is 100 rows in the table
      // however many times each of them was reopened.
      const { data } = await api.get(`/my/user/history/companies`, {
        params: { limit: 100 },
      });
      setCompanies(data);
    } catch (error) {
      setCompanies([]);
      setError(error as Error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <>
      <ResizablePanelGroup orientation="horizontal" className="w-full">
        <ResizablePanel defaultSize="50%" className="relative">
          <div className="overflow-auto absolute top-0 left-0 flex-1 p-8 pt-6 compact:p-6 compact:pt-4 space-y-4 w-full h-full">
            <div className="flex-1 p-8 pt-0 space-y-5">
              <div className="flex justify-between">
                <div className="space-y-2">
                  <h2 className="text-2xl font-bold tracking-tight">
                    {t("prospecting.history.title")}
                  </h2>
                  <p className="text-muted-foreground">
                    {t("prospecting.history.subtitle")}
                  </p>
                </div>
                <MapControls
                  companies={companies}
                  isMapOpened={isMapOpened}
                  onMapOpenChange={setIsMapOpened}
                  onItineraryStateChange={setItineraryState}
                />
              </div>

              <Separator className="my-6" />

              {!isLoading && error && <ErrorMessage />}
              {(isLoading || !error) && (
                <DataTableCompanies
                  companies={companies}
                  selected={companySelected}
                  onSortedDataChange={itineraryState?.setSortedCompanies}
                  isLoading={isLoading}
                  emptyText={t("prospecting.history.empty")}
                  additionalColumns={[
                    [
                      t("prospecting.recentCompanies.table.lastVisit"),
                      (item: CompanyDTO) =>
                        `${
                          item.lastVisited
                            ? new Date(item.lastVisited).toLocaleString()
                            : ""
                        }`,
                    ],
                  ]}
                />
              )}
            </div>
          </div>
        </ResizablePanel>
        {isMapOpened && (
          <>
            <ResizableHandle
              withHandle
              className="transition-all hover:bg-gray-500 active:bg-gray-200"
            />
            <ResizablePanel defaultSize="20%" maxSize="50%">
              <MapCompanies
                companies={itineraryState?.companiesForMap || companies}
                selected={companySelected}
                setSelected={setCompanySelected}
                shouldCalculateItinerary={
                  itineraryState?.shouldCalculateItinerary || false
                }
              />
            </ResizablePanel>
          </>
        )}
      </ResizablePanelGroup>
    </>
  );
}
